/**
 * Live password strength meter (zxcvbn). UX only — the server (passwordPolicy.js)
 * is the authoritative gate (length ≥ 10, zxcvbn score ≥ 3, HIBP breach screen).
 */
import React, { useMemo } from 'react';
import zxcvbn from 'zxcvbn';

const MIN_LENGTH = 10;
const MIN_SCORE = 3;
const LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];
const COLORS = ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#10b981'];

/** Client-side gate mirroring the server minimums (breach check stays server-side). */
export function isPasswordAcceptable(password, userInputs = []) {
    if (!password || password.length < MIN_LENGTH) return false;
    return zxcvbn(password, userInputs.filter(Boolean)).score >= MIN_SCORE;
}

export default function PasswordStrength({ password, userInputs = [] }) {
    const result = useMemo(
        () => (password ? zxcvbn(password, userInputs.filter(Boolean)) : null),
        [password, userInputs]
    );
    if (!password) return null;

    const tooShort = password.length < MIN_LENGTH;
    // Don't flatter a short password with a high bar.
    const score = tooShort ? Math.min(result.score, 1) : result.score;
    const warning = result.feedback?.warning;
    const suggestion = result.feedback?.suggestions?.[0];

    return (
        <div className="mt-2">
            <div className="flex gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                    <div
                        key={i}
                        className="h-1.5 flex-1 rounded-full transition-colors"
                        style={{ background: i <= score ? COLORS[score] : 'rgba(255,255,255,0.12)' }}
                    />
                ))}
            </div>
            <div className="flex items-center justify-between mt-1">
                <span className="text-xs font-medium" style={{ color: COLORS[score] }}>{LABELS[score]}</span>
                {tooShort && <span className="text-xs text-text-muted">Min {MIN_LENGTH} characters</span>}
            </div>
            {(warning || suggestion) && (
                <p className="text-xs text-text-muted mt-1">{warning || suggestion}</p>
            )}
        </div>
    );
}
